import React from 'react';
import {
  Table,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  Badge,
  Center,
  Avatar
} from '@mantine/core';
import { humanize } from 'inflection';
import Can from './Can';
import { formatDate } from '../lib/date-time-formatters';
import { RequestData } from '../types/request.types';
import RequestIcon from './RequestIcon';
import {
  IconChevronDown,
  IconChevronUp,
  IconSelector
} from '@tabler/icons-react';
import RequestStatusBadge from './RequestStatusBadge';
import { SortDirection } from '../types/interface.types';
import { useRequests, useRequestsDispatch } from '../reducers/requestsReducer';
import UserAvatarWithCard from './UserAvatarWithCard';

import classes from './RequestsTable.module.css';

interface RequestTableProps {
  onRowClick?: (request: RequestData) => void;
}

interface ThProps {
  children?: React.ReactNode;
  sorted?: boolean;
  direction?: SortDirection;
  onSort?: () => void;
}

const Th = ({ children, direction, sorted, onSort }: ThProps) => {
  const Icon = !!sorted ? (direction === 'asc' ? IconChevronDown : IconChevronUp) : IconSelector;
  
  return (
    <Table.Th p={0}>
      <UnstyledButton
        w="100%"
        px="sm"
        py="xs"
        onClick={onSort}
        className={classes.tableHeader}
      >
        <Group justify="space-between">
          <Text fw={500} fz="sm">
            {children}
          </Text>
          {onSort && (
            <Center>
              <Icon size={16} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </Table.Th>
  )
}

export default function RequestsTable({
  onRowClick,
}: RequestTableProps) {
  const [sortKey, setSortKey] = React.useState<string | null>(null);
  const [sortDir, setSortDir] = React.useState<SortDirection>('asc');
  const { requests } = useRequests();
  const dispatch = useRequestsDispatch();
  let rows;

  const handleSort = (key: string) => {
    let newDir: SortDirection = sortKey === key && sortDir === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDir(newDir);
    dispatch({ type: 'SET_SORT', sort: `${key}:${newDir}` });
  };

  if (requests && requests.length > 0) {
    rows = requests.map((row: RequestData) => {
      let avatars;

      if (row.assigned_users) {
        avatars = row.assigned_users.map(au => (
          <UserAvatarWithCard key={au.id} user={au} />
        ));
      }
      
      return (
        <Table.Tr
          className={classes.tr}
          key={`${row.type}-${row.id}`}
          onClick={() => onRowClick && onRowClick(row)}
        >
          <Table.Td miw="20%">
            <Badge
              leftSection={
                <RequestIcon requestType={row.type} size={20} stroke={1.25} />
              }
              variant="default"
              size="lg"
            >
              {humanize(row.type.split('_').slice(0, -1).join('_'), false)}
            </Badge>
          </Table.Td>
          <Table.Td miw="30%">{row.name}</Table.Td>
          <Table.Td miw="11%">
            {row.user.name ? row.user.name : row.user.username}
          </Table.Td>
          <Can do="update_assignment" on="Request">
            <Table.Td miw="11%">
              <Avatar.Group>
                {avatars}
              </Avatar.Group>
            </Table.Td>
          </Can>
          <Table.Td miw="17%">
            <RequestStatusBadge status={row.status} />
          </Table.Td>
          <Table.Td miw="11%" style={{whiteSpace: 'nowrap'}}>
            {formatDate(row.created_at)}
          </Table.Td>
        </Table.Tr>
      );
    })
  }
  
  return (
    <ScrollArea>
      <Table
        w="100%"
        highlightOnHover
        horizontalSpacing="sm"
        verticalSpacing="xs"
        layout="auto"
      >
        <Table.Thead>
          <Table.Tr>
            <Th
              direction={sortDir}
              sorted={sortKey === 'type'}
              onSort={() => handleSort('type')}
            >
              Type
            </Th>
            <Th>Name</Th>
            <Th>Submitted by</Th>
            <Can do="update_assignment" on="Request">
              <Th sorted={false}>Assigned To</Th>
            </Can>
            <Th
              direction={sortDir}
              sorted={sortKey === 'status'}
              onSort={() => handleSort('status')}
            >
              Status
            </Th>
            <Th
              direction={sortDir}
              sorted={sortKey === 'submitted'}
              onSort={() => handleSort('submitted')}
            >
              Submitted
            </Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
}
